/**
 * Tooltip Component
 * Hover on desktop, tap on mobile web. Positions above the trigger by default.
 */

import { type ReactElement, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  /** Tooltip body — can be a string or JSX */
  content: ReactNode;
  children: ReactElement;
  /** Max width in px (default 260) */
  maxWidth?: number;
}

export function Tooltip({ content, children, maxWidth = 260 }: TooltipProps): ReactElement {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const hideTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);

  const show = useCallback(() => {
    clearTimeout(hideTimeout.current);
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({
      top: rect.top - 8, // 8px above trigger
      left: rect.left + rect.width / 2,
    });
    setVisible(true);
  }, []);

  const hide = useCallback(() => {
    hideTimeout.current = setTimeout(() => setVisible(false), 120);
  }, []);

  // Toggle on tap (mobile web)
  const toggle = useCallback(() => {
    if (visible) {
      setVisible(false);
    } else {
      show();
    }
  }, [visible, show]);

  // Close on outside tap when visible
  useEffect(() => {
    if (!visible) return;
    const handleOutside = (e: PointerEvent) => {
      if (triggerRef.current?.contains(e.target as Node)) return;
      setVisible(false);
    };
    document.addEventListener('pointerdown', handleOutside);
    return () => document.removeEventListener('pointerdown', handleOutside);
  }, [visible]);

  // Cleanup timeout
  useEffect(() => () => clearTimeout(hideTimeout.current), []);

  return (
    <>
      <div
        ref={triggerRef}
        className="inline-flex"
        onMouseEnter={show}
        onMouseLeave={hide}
        onClick={toggle}
      >
        {children}
      </div>
      {visible &&
        pos &&
        createPortal(
          <div
            className="fixed z-[100] pointer-events-none animate-in fade-in duration-150"
            style={{
              top: pos.top,
              left: pos.left,
              transform: 'translate(-50%, -100%)',
              maxWidth,
            }}
          >
            <div className="px-3 py-2 rounded-lg bg-theme-surface border border-theme-border shadow-xl shadow-black/40 text-xs text-theme-text leading-relaxed">
              {content}
              {/* Arrow pointing down */}
              <div className="absolute left-1/2 -translate-x-1/2 -bottom-1.5 w-3 h-3 bg-theme-surface border-r border-b border-theme-border rotate-45" />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
